/**
 * React Query hooks for Tags System
 * 
 * Features:
 * - useUserTags: Fetch user's tags with caching
 * - useCreateTag: Create new tags with optimistic updates
 * - useUpdateTag: Update existing tags
 * - useDeleteTag: Soft delete tags
 * - useTaggedRecords: Fetch records filtered by tags
 * - Tag management with automatic cache invalidation
 * - Optimistic updates for better UX
 * - Error handling and loading states
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tag } from "@/integrations/supabase/types";
import tagService from "@/services/tagService";
import { RecordWithTemplate, recordService } from "@/integrations/records/recordService";
import { PaginationParams, PaginatedResponse } from "@/types";

// Define tag operation types
export interface CreateTagData {
  name: string;
  color: string;
}

export interface UpdateTagData {
  name?: string;
  color?: string;
  sort_order?: number;
}

// Mutation context types
interface TagMutationContext {
  previousTags?: Tag[];
  optimisticTag?: Tag;
}

// Query Keys
export const tagKeys = {
  all: ['tags'] as const,
  userTags: (userId: string) => [...tagKeys.all, 'user', userId] as const,
  taggedRecords: (userId: string, tagIds: string[]) => [...tagKeys.all, 'records', userId, tagIds] as const,
};

// ==========================================
// useUserTags - Fetch user's tags
// ==========================================

interface UseUserTagsOptions {
  enabled?: boolean;
  userId: string;
  includeDeleted?: boolean;
}

export function useUserTags({
  enabled = true,
  userId,
  includeDeleted = false,
}: UseUserTagsOptions) {
  // Early validation
  if (!userId) {
    throw new Error("useUserTags: userId is required");
  }

  return useQuery<Tag[]>({
    queryKey: tagKeys.userTags(userId),
    queryFn: () => tagService.getUserTags(userId),
    enabled: enabled && !!userId,
    staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnMount: false, // Tags don't change frequently
    refetchOnWindowFocus: false,
  });
}

// ==========================================
// useCreateTag - Create new tag
// ==========================================

interface UseCreateTagOptions {
  onSuccess?: (tag: Tag) => void;
  onError?: (error: Error) => void;
}

export function useCreateTag({ onSuccess, onError }: UseCreateTagOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<Tag, Error, { userId: string; data: CreateTagData }>({
    mutationFn: ({ userId, data }) => tagService.createTag(userId, data),
    
    onMutate: async ({ userId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: tagKeys.userTags(userId) });

      // Snapshot the previous value
      const previousTags = queryClient.getQueryData<Tag[]>(tagKeys.userTags(userId));

      // Optimistically update to the new value
      const optimisticTag: Tag = {
        id: `temp-${Date.now()}`, // Temporary ID
        name: data.name,
        color: data.color,
        user_id: userId,
        created_at: new Date().toISOString(),
        deleted_at: null,
        sort_order: previousTags?.length || 0,
      };

      queryClient.setQueryData<Tag[]>(
        tagKeys.userTags(userId), 
        (old) => [...(old || []), optimisticTag]
      );

      // Return a context object with the snapshotted value
      return { previousTags, optimisticTag } as TagMutationContext;
    },

    onError: (error, { userId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context && 'previousTags' in context && context.previousTags) {
        queryClient.setQueryData(tagKeys.userTags(userId), context.previousTags);
      }
      onError?.(error);
    },

    onSuccess: (newTag, { userId }, context) => {
      // Replace optimistic tag with real tag
      queryClient.setQueryData<Tag[]>(
        tagKeys.userTags(userId), 
        (old) => {
          if (!old) return [newTag];
          return old.map(tag => {
            const optimisticId = context && 'optimisticTag' in context && context.optimisticTag ? context.optimisticTag.id : null;
            return tag.id === optimisticId ? newTag : tag;
          });
        }
      );
      onSuccess?.(newTag);
    },

    onSettled: (_, __, { userId }) => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: tagKeys.userTags(userId) });
    },
  });
}

// ==========================================
// useUpdateTag - Update existing tag
// ==========================================

interface UseUpdateTagOptions {
  onSuccess?: (tag: Tag) => void;
  onError?: (error: Error) => void;
}

export function useUpdateTag({ onSuccess, onError }: UseUpdateTagOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<Tag, Error, { tagId: string; data: UpdateTagData; userId: string }>({
    mutationFn: ({ tagId, data }) => tagService.updateTag(tagId, data),
    
    onMutate: async ({ tagId, data, userId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: tagKeys.userTags(userId) });

      // Snapshot the previous value
      const previousTags = queryClient.getQueryData<Tag[]>(tagKeys.userTags(userId));

      // Optimistically update to the new value
      queryClient.setQueryData<Tag[]>(
        tagKeys.userTags(userId), 
        (old) => {
          if (!old) return [];
          return old.map(tag => 
            tag.id === tagId ? { ...tag, ...data } : tag
          );
        }
      );

      return { previousTags } as TagMutationContext;
    },

    onError: (error, { userId }, context) => {
      // If the mutation fails, use the context to roll back
      if (context && 'previousTags' in context && context.previousTags) {
        queryClient.setQueryData(tagKeys.userTags(userId), context.previousTags);
      }
      onError?.(error);
    },

    onSuccess: (updatedTag, { userId }) => {
      // Update the cache with the real updated tag
      queryClient.setQueryData<Tag[]>(
        tagKeys.userTags(userId), 
        (old) => {
          if (!old) return [updatedTag];
          return old.map(tag => 
            tag.id === updatedTag.id ? updatedTag : tag
          );
        }
      );
      onSuccess?.(updatedTag);
    },

    onSettled: (_, __, { userId }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: tagKeys.userTags(userId) });
    },
  });
}

// ==========================================
// useDeleteTag - Soft delete tag
// ==========================================

interface UseDeleteTagOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useDeleteTag({ onSuccess, onError }: UseDeleteTagOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { tagId: string; userId: string }>({
    mutationFn: ({ tagId }) => tagService.deleteTag(tagId),
    
    onMutate: async ({ tagId, userId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: tagKeys.userTags(userId) });

      // Snapshot the previous value
      const previousTags = queryClient.getQueryData<Tag[]>(tagKeys.userTags(userId));

      // Optimistically remove the tag
      queryClient.setQueryData<Tag[]>(
        tagKeys.userTags(userId), 
        (old) => {
          if (!old) return [];
          return old.filter(tag => tag.id !== tagId);
        }
      );

      return { previousTags } as TagMutationContext;
    },

    onError: (error, { userId }, context) => {
      // If the mutation fails, use the context to roll back
      if (context && 'previousTags' in context && context.previousTags) {
        queryClient.setQueryData(tagKeys.userTags(userId), context.previousTags);
      }
      onError?.(error);
    },

    onSuccess: () => {
      onSuccess?.();
    },

    onSettled: (_, __, { userId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: tagKeys.userTags(userId) });
      queryClient.invalidateQueries({ queryKey: ['records'] }); // Also invalidate records that might have this tag
    },
  });
}

// ==========================================
// useTaggedRecords - Fetch records by tags
// ==========================================

interface UseTaggedRecordsOptions extends Omit<PaginationParams, 'tagFilter'> {
  enabled?: boolean;
  tagIds: string[];
}

export function useTaggedRecords({
  enabled = true,
  userId,
  tagIds,
  page = 1,
  limit = 10,
  search,
  statusFilter = "all",
  sortBy = "newest",
  sortDirection = "desc",
}: UseTaggedRecordsOptions) {
  // Early validation
  if (!userId) {
    throw new Error("useTaggedRecords: userId is required");
  }

  const params: PaginationParams = {
    page,
    limit,
    search,
    statusFilter,
    sortBy,
    sortDirection,
    userId,
    tagFilter: tagIds,
  };

  return useQuery<PaginatedResponse<RecordWithTemplate>>({
    queryKey: tagKeys.taggedRecords(userId, tagIds),
    queryFn: () => recordService.getPaginatedRecords(params),
    enabled: enabled && !!userId && tagIds.length > 0,
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000, // Consider fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: true,
  });
}

// ==========================================
// Utility hooks for specific operations
// ==========================================

/**
 * Hook for managing tag-record relationships
 */
export function useTagRecordOperations() {
  const queryClient = useQueryClient();

  const addTagsToRecord = useMutation<void, Error, { recordId: string; tagIds: string[] }>({
    mutationFn: ({ recordId, tagIds }) => tagService.addTagsToRecord(recordId, tagIds),
    onSuccess: () => {
      // Invalidate both tags and records queries
      queryClient.invalidateQueries({ queryKey: ['records'] });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });

  const removeTagsFromRecord = useMutation<void, Error, { recordId: string; tagIds: string[] }>({
    mutationFn: ({ recordId, tagIds }) => tagService.removeTagsFromRecord(recordId, tagIds),
    onSuccess: () => {
      // Invalidate both tags and records queries
      queryClient.invalidateQueries({ queryKey: ['records'] });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });

  return {
    addTagsToRecord,
    removeTagsFromRecord,
  };
}

/**
 * Hook to get tag statistics (usage counts, etc.)
 */
export function useTagStats(userId: string) {
  return useQuery<Array<{ tag: Tag; usage_count: number }>>({
    queryKey: [...tagKeys.userTags(userId), 'stats'],
    queryFn: async () => {
      const stats = await tagService.getTagUsageStats(userId);
      return stats;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // Stats don't change frequently
    gcTime: 15 * 60 * 1000,
  });
}